import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TypedDateInput } from "@/components/TypedDateInput";

import { API, addDays, errorMessage, TERM_DAYS, todayIso } from "./payrollUtils";

const initialForm = (profile, mode) => {
  const payment = mode === "create" ? {} : profile.featured_payment || {};
  if (profile.type === "in_house") {
    return {
      amount: payment.amount ?? "",
      status: payment.status || "unpaid",
      payment_date: payment.payment_date || "",
      notes: payment.notes || "",
    };
  }
  const submitted = payment.submitted_date || (mode === "create" ? todayIso() : "");
  const due =
    payment.due_date ||
    (mode === "create" ? addDays(submitted, TERM_DAYS[profile.payment_term] || 14) : "");
  return {
    amount: payment.amount ?? "",
    status: payment.status || "unpaid",
    submitted_date: submitted,
    due_date: due,
    paid_date: payment.paid_date || "",
    job: payment.job || "",
    notes: payment.notes || "",
  };
};

export default function PayrollPaymentQuickEditor({ profile, mode = "edit", onClose, onSaved }) {
  const payment = mode === "edit" ? profile.featured_payment : null;
  const [form, setForm] = useState(() => initialForm(profile, mode));
  const [saving, setSaving] = useState(false);

  if (mode === "edit" && !payment) {
    return null;
  }

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { amount: Number(form.amount || 0), status: form.status, notes: form.notes };
      if (profile.type === "in_house") {
        body.payment_date = form.payment_date || "";
      } else {
        body.submitted_date = form.submitted_date || "";
        body.due_date = form.due_date || "";
        body.paid_date = form.paid_date || "";
        body.job = form.job || "";
      }
      // If status flips to paid and no paid/payment date supplied, default to today.
      if (body.status === "paid") {
        if (profile.type === "in_house" && !body.payment_date) body.payment_date = todayIso();
        if (profile.type === "contractor" && !body.paid_date) body.paid_date = todayIso();
      }
      if (mode === "create") {
        // Backend rejects wrong-type fields with empty strings for contractor; keep only relevant keys.
        if (profile.type === "in_house") {
          body.rate_used = null;
          body.hours = null;
          body.days = null;
        }
        await axios.post(`${API}/payroll/profiles/${profile.id}/payments`, body);
        toast.success("Payment recorded.");
      } else {
        await axios.patch(
          `${API}/payroll/profiles/${profile.id}/payments/${payment.id}`,
          body,
        );
        toast.success("Payment updated.");
      }
      await onSaved();
      onClose();
    } catch (requestError) {
      toast.error(
        errorMessage(
          requestError,
          mode === "create" ? "Payment could not be created." : "Payment could not be updated.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const heading =
    mode === "create" ? `New payment — ${profile.name}` : `Quick edit — ${profile.name}`;
  const description =
    mode === "create"
      ? `Record a new ${profile.type === "in_house" ? "In-House" : "contractor"} payment for ${profile.name}. Only the essentials — everything else stays in Record.`
      : `Update this payment's amount, status, date, or notes. Everything else for ${profile.name} (profile details and full history) lives in Record.`;

  return (
    <Dialog onOpenChange={onClose} open>
      <DialogContent data-testid="payroll-quick-edit-editor">
        <DialogHeader>
          <DialogTitle>{heading}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <label htmlFor="payroll-quick-edit-amount">
          Amount
          <input
            data-testid="payroll-quick-edit-amount"
            id="payroll-quick-edit-amount"
            min="0"
            onChange={(event) => update({ amount: event.target.value })}
            step="0.01"
            type="number"
            value={form.amount}
          />
        </label>

        <label htmlFor="payroll-quick-edit-status">
          Status
          <select
            data-testid="payroll-quick-edit-status"
            id="payroll-quick-edit-status"
            onChange={(event) => update({ status: event.target.value })}
            value={form.status}
          >
            <option value="unpaid">Unpaid</option>
            <option value="paid">Paid</option>
          </select>
        </label>

        {profile.type === "in_house" ? (
          <label htmlFor="payroll-quick-edit-payment-date">
            Payment date
            <TypedDateInput
              data-testid="payroll-quick-edit-payment-date"
              id="payroll-quick-edit-payment-date"
              onChange={(value) => update({ payment_date: value })}
              value={form.payment_date}
            />
          </label>
        ) : (
          <>
            <label htmlFor="payroll-quick-edit-submitted-date">
              Submitted date
              <TypedDateInput
                data-testid="payroll-quick-edit-submitted-date"
                id="payroll-quick-edit-submitted-date"
                onChange={(value) => update({ submitted_date: value })}
                value={form.submitted_date}
              />
            </label>
            <label htmlFor="payroll-quick-edit-due-date">
              Due date
              <TypedDateInput
                data-testid="payroll-quick-edit-due-date"
                id="payroll-quick-edit-due-date"
                onChange={(value) => update({ due_date: value })}
                value={form.due_date}
              />
            </label>
            <label htmlFor="payroll-quick-edit-paid-date">
              Paid date
              <TypedDateInput
                data-testid="payroll-quick-edit-paid-date"
                id="payroll-quick-edit-paid-date"
                onChange={(value) => update({ paid_date: value })}
                value={form.paid_date}
              />
            </label>
            <label htmlFor="payroll-quick-edit-job">
              Job
              <input
                data-testid="payroll-quick-edit-job"
                id="payroll-quick-edit-job"
                onChange={(event) => update({ job: event.target.value })}
                placeholder="e.g. Maple St bore"
                value={form.job}
              />
            </label>
          </>
        )}

        <label htmlFor="payroll-quick-edit-notes">
          Notes
          <textarea
            data-testid="payroll-quick-edit-notes"
            id="payroll-quick-edit-notes"
            onChange={(event) => update({ notes: event.target.value })}
            value={form.notes}
          />
        </label>

        <DialogFooter>
          <Button
            data-testid="payroll-quick-edit-cancel"
            onClick={onClose}
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            data-testid="payroll-quick-edit-save"
            disabled={saving}
            onClick={save}
            type="button"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
